/* =========================
   12) src/lib/api.js
   - NO CHANGE (exactly your file)
========================= */
import axios from "axios";
import { openDB } from "./db";

/**
 * Single source of truth for backend base URL.
 *
 * Preferred env:
 *   REACT_APP_API_URL=https://your-backend.example.com
 * Backward-compatible env:
 *   REACT_APP_BACKEND_URL=https://your-backend.example.com
 *
 * Behavior:
 * - If env is missing, we DO NOT crash the whole app.
 * - We fallback to window.location.origin (useful for local/proxy or preview),
 *   but we warn loudly so you set env correctly.
 */
const RAW_BACKEND_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL;
const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

const resolvedBackendUrl =
  RAW_BACKEND_URL ||
  (isLocalHost ? "http://127.0.0.1:8000" : window.location.origin);

const BACKEND_URL = resolvedBackendUrl.replace(/\/+$/, "");
if (!RAW_BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.warn(
    "REACT_APP_BACKEND_URL is missing. " +
      `Using ${BACKEND_URL}. ` +
      "Set REACT_APP_API_URL in frontend/.env for explicit control."
  );
}

const API_BASE = `${BACKEND_URL}/api`;

let authToken = null;
let lastRequestId = null;

export const getLastRequestId = () => lastRequestId;

const extractRequestId = (headers = {}) =>
  headers["x-request-id"] || headers["X-Request-ID"] || headers["rndr-id"] || null;

const looksTechnicalError = (text = "") => {
  const s = String(text || "").toLowerCase();
  return (
    s.includes("traceback") ||
    s.includes("exception") ||
    s.includes("stack") ||
    s.includes("sqlalchemy") ||
    s.includes("asyncpg") ||
    s.includes("line ") ||
    s.includes("file \"") ||
    s.includes(" at ") ||
    s.includes("duplicatepreparedstatementerror")
  );
};

const sanitizeErrorDetailForUser = (status, detail) => {
  const genericByStatus = {
    400: "Invalid request. Please check input and retry.",
    401: "Session expired. Please login again.",
    402: "Subscription action required. Contact owner/admin.",
    403: "Access denied for this action.",
    404: "Requested record was not found.",
    409: "Data conflict detected. Reload and retry.",
    422: "Please correct the form fields and retry.",
    429: "Too many requests. Please wait a few seconds.",
  };

  if (status >= 500) return "Server is temporarily unavailable. Please retry.";
  if (typeof detail === "string" && detail.trim()) {
    if (looksTechnicalError(detail)) {
      return genericByStatus[status] || "Request failed. Please retry.";
    }
    return detail;
  }
  if (typeof detail === "object" && detail !== null) {
    const msg = detail.message || detail.error || null;
    if (typeof msg === "string" && msg.trim() && !looksTechnicalError(msg)) {
      return msg;
    }
  }
  return genericByStatus[status] || "Request failed. Please retry.";
};

export const getFriendlyErrorMessage = (error, fallback = "Request failed") => {
  if (!error) return fallback;
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const detailText = sanitizeErrorDetailForUser(status, detail);

  if (!status) return "Network issue. Check internet and retry.";
  if (status === 401) return "Session expired. Please login again.";
  if (status === 402) return detailText || "Subscription action required. Contact owner/admin.";
  if (status === 403) return detailText || "Access denied for this action.";
  if (status === 404) return detailText || "Requested record was not found.";
  if (status === 409) return detailText || "This record changed elsewhere. Reload and retry.";
  if (status === 422) return detailText || "Please correct the form fields and retry.";
  if (status === 429) return "Too many requests. Please wait a few seconds.";
  if (status >= 500) return "Server is temporarily unavailable. Please retry.";
  return detailText || fallback;
};

/* =========================
   AUTH TOKEN HANDLING
========================= */

export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    localStorage.setItem("authToken", token);
  } else {
    localStorage.removeItem("authToken");
  }
};

export const getAuthToken = () => {
  if (!authToken) {
    authToken = localStorage.getItem("authToken");
  }
  return authToken;
};

/* =========================
   AXIOS CLIENT
========================= */

const apiClient = axios.create({
  baseURL: API_BASE,
  // 60 s — gives Render free-tier enough time to cold-start before any
  // individual API call is declared failed.
  timeout: 60000,
});

const mapProductionToLegacyProgram = (p) => {
  const size = Number(p?.circle_size_inch);
  const safeSize = Number.isFinite(size) ? size : null;
  const rate = Number(p?.job_rate_per_kg);
  const safeRate = Number.isFinite(rate) ? rate : null;

  return {
    id: p.id,
    coil_in_id: p.coil_in_id,
    coil_id: p.coil_id,
    allocation_mode: "EQUAL",
    circle_sizes: safeSize ? [safeSize] : [],
    allocations: null,
    notes: p.remarks || null,
    rate_per_kg: safeRate,
    rates_by_circle_size: safeSize != null && safeRate != null ? { [String(safeSize)]: safeRate } : null,
    is_active: p.is_active,
    created_at: p.created_at,
    created_by: p.created_by,
    source: "PRODUCTION_PROGRAMS_CANONICAL",
    legacy_compat: true,
    program_no: p.program_no,
    status: p.status,
    expected_recovery_percent: p.expected_recovery_percent ?? null,
  };
};

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    lastRequestId = extractRequestId(response.headers || {});
    return response;
  },
  (error) => {
    lastRequestId = extractRequestId(error?.response?.headers || {});
    if (error?.response?.status && error?.response?.data) {
      error.response.data.detail = sanitizeErrorDetailForUser(
        error.response.status,
        error.response.data.detail
      );
    }
    if (error.response?.status === 401) {
      setAuthToken(null);
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

/* =========================
   OFFLINE SYNC QUEUE
========================= */

const safeUUID_api = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
};

export const queueOperation = async (operation) => {
  const db = await openDB();
  const tx = db.transaction("syncQueue", "readwrite");
  await tx.objectStore("syncQueue").add({
    ...operation,
    id: safeUUID_api(),
    timestamp: new Date().toISOString(),
    status: "pending",
  });
  await tx.done;
};

export const getSyncQueue = async () => {
  const db = await openDB();
  return await db.getAll("syncQueue");
};

export const removeSyncQueueItem = async (id) => {
  const db = await openDB();
  await db.delete("syncQueue", id);
};

export const processSyncQueue = async () => {
  if (!navigator.onLine) return;

  const queue = await getSyncQueue();

  for (const item of queue) {
    try {
      const { method, endpoint, data } = item;
      await apiClient({ method, url: endpoint, data });
      await removeSyncQueueItem(item.id);
    } catch (err) {
      // Keep it in queue; will retry next time online
      // eslint-disable-next-line no-console
      console.error("Sync failed for item:", item, err);
    }
  }
};

window.addEventListener("online", () => {
  processSyncQueue();
});

/* =========================
   API OBJECT (SINGLE SOURCE)
========================= */

export const api = {
  /* -------- AUTH -------- */
  auth: {
    login: (data) => apiClient.post("/auth/login", data),
    signup: (data) => apiClient.post("/auth/signup", data),
    me: () => apiClient.get("/auth/me"),
  },

  subscriptions: {
    listPlans: () => apiClient.get("/subscriptions/plans"),
    me: () => apiClient.get("/subscriptions/me"),
  },

  /* -------- USERS -------- */
  users: {
    list: () => apiClient.get("/users"),
    create: (data) => apiClient.post("/users", data),
    get: (id) => apiClient.get(`/users/${id}`),
    update: (id, data) => apiClient.patch(`/users/${id}`, data),
    resetPassword: (id, password) => {
      const formData = new FormData();
      formData.append("new_password", password);
      return apiClient.post(`/users/${id}/reset-password`, formData);
    },
  },

  /* -------- PARTIES -------- */
  parties: {
    list: (params) => apiClient.get("/parties", { params }),
    create: (data) => apiClient.post("/parties", data),
    get: (id) => apiClient.get(`/parties/${id}`),
    update: (id, data) => apiClient.patch(`/parties/${id}`, data),
    delete: (id) => apiClient.delete(`/parties/${id}`),
  },

  /* -------- COIL IN -------- */
  coilIns: {
    list: (params) => apiClient.get("/coil-ins", { params }),
    create: (data) => apiClient.post("/coil-ins", data),
    get: (id) => apiClient.get(`/coil-ins/${id}`),
    delete: (id) => apiClient.delete(`/coil-ins/${id}`),
    updateKanta: (coilInId, coilId, data) => 
      apiClient.patch(`/coil-ins/${coilInId}/coils/${coilId}/kanta`, data),
    updateFactoryWeighment: (coilInId, coilId, data) => 
      apiClient.patch(`/coil-ins/${coilInId}/coils/${coilId}/factory-weighment`, data),
    updateLifecycle: (coilInId, coilId, data) => 
      apiClient.patch(`/coil-ins/${coilInId}/coils/${coilId}/lifecycle`, data),
  },

  /* -------- DASHBOARD -------- */
  dashboard: {
    getStats: () => apiClient.get("/dashboard/stats"),
  },

  /* -------- FILE UPLOAD -------- */
  upload: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post("/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  /* -------- AUDIT LOGS -------- */
  auditLogs: {
    list: (params) => apiClient.get("/audit-logs", { params }),
  },

  /* -------- PRODUCTION PROGRAMS (Phase-1 Prompt-2A) -------- */
  productionPrograms: {
    list: (params) => apiClient.get("/production-programs", { params }),
    create: (data) => apiClient.post("/production-programs", data),
    get: (id) => apiClient.get(`/production-programs/${id}`),
    update: (id, data) => apiClient.patch(`/production-programs/${id}`, data),
    delete: (id) => apiClient.delete(`/production-programs/${id}`),
    getByCoil: (coilInId, coilId) => apiClient.get(`/coils/${coilInId}/${coilId}/programs`),
    // Recovery logs
    addRecoveryLog: (programId, data) => apiClient.post(`/production-programs/${programId}/recovery-logs`, data),
    getRecoveryLogs: (programId) => apiClient.get(`/production-programs/${programId}/recovery-logs`),
  },

  /* -------- COIL ESTIMATES -------- */
  coilEstimates: {
    get: (coilInId, coilId) => apiClient.get(`/coils/${coilInId}/${coilId}/estimated`),
  },

  /* -------- COIL DISPATCH (Phase-2) -------- */
  coilDispatch: {
    add: (coilInId, coilId, data) => apiClient.post(`/coils/${coilInId}/${coilId}/dispatch`, data),
    list: (coilInId, coilId) => apiClient.get(`/coils/${coilInId}/${coilId}/dispatch`),
  },

  /* -------- RECONCILIATION (Phase-2) -------- */
  reconciliation: {
    getSummary: (coilInId, coilId) => apiClient.get(`/coils/${coilInId}/${coilId}/reconciliation-summary`),
  },

  /* -------- COIL SETTLEMENTS (Phase-E) -------- */
  coilSettlements: {
    list: (coilInId, coilId) => apiClient.get(`/coils/${coilInId}/${coilId}/settlements`),
    create: (coilInId, coilId, data) => apiClient.post(`/coils/${coilInId}/${coilId}/settlements`, data),
  },

  /* -------- FINALIZE COIL (Phase-2 Completion) -------- */
  coilFinalize: {
    finalize: (coilInId, coilId, data) => apiClient.patch(`/coils/${coilInId}/${coilId}/finalize`, data),
  },

  /* -------- REOPEN COIL (Admin Only) -------- */
  coilReopen: {
    reopen: (coilInId, coilId, data) => apiClient.patch(`/coils/${coilInId}/${coilId}/reopen`, data),
  },

  /* =========================
     PASS 2 MODULES
  ========================= */

  /* -------- PROGRAMS -------- */
  programs: {
    list: async (params) => {
      const response = await apiClient.get("/production-programs", { params });
      return {
        ...response,
        data: (response.data || []).map(mapProductionToLegacyProgram),
      };
    },
    create: async (data) => {
      const sizes = Array.isArray(data?.circle_sizes) ? data.circle_sizes : [];
      if (!data?.coil_in_id || !data?.coil_id || sizes.length === 0) {
        throw new Error("coil_in_id, coil_id and at least one circle size are required");
      }

      const ratesMap = data?.rates_by_circle_size || {};
      const requests = sizes.map((sizeRaw) => {
        const size = Number(sizeRaw);
        if (!Number.isFinite(size) || size <= 0) return null;

        const mappedRate = ratesMap[String(size)] ?? ratesMap[String(Number(size))];
        const fallbackRate = data?.rate_per_kg;
        const rateVal = mappedRate ?? fallbackRate ?? 0;
        const rate = Number(rateVal);

        return apiClient.post("/production-programs", {
          coil_in_id: data.coil_in_id,
          coil_id: data.coil_id,
          circle_size_inch: size,
          expected_recovery_percent: null,
          job_rate_per_kg: Number.isFinite(rate) && rate >= 0 ? rate : 0,
          remarks: data?.notes || null,
        });
      }).filter(Boolean);

      if (requests.length === 0) {
        throw new Error("No valid circle sizes provided");
      }

      const created = await Promise.all(requests);
      const first = created[0];
      return {
        ...first,
        data: mapProductionToLegacyProgram(first.data),
      };
    },
  },

  /* -------- LEGACY PROGRAMS (Frozen path during migration) -------- */
  legacyPrograms: {
    list: (params) => apiClient.get("/programs", { params }),
    create: (data) => apiClient.post("/programs", data),
  },

  /* -------- PROGRAM CHANGE REQUESTS -------- */
  programChangeRequests: {
    list: (params) => apiClient.get("/program-change-requests", { params }),
    create: (data) => apiClient.post("/program-change-requests", data),
    approve: (id, data) => apiClient.post(`/program-change-requests/${id}/approve`, data),
  },

  /* -------- JOBS -------- */
  jobs: {
    list: (params) => apiClient.get("/jobs", { params }),
    create: (data) => apiClient.post("/jobs", data),
    update: (id, data) => apiClient.patch(`/jobs/${id}`, data),
    addDieChange: (id, data) => apiClient.post(`/jobs/${id}/die-change`, data),
    addIssue: (id, data) => apiClient.post(`/jobs/${id}/issue`, data),
    addOutputLot: (id, data) => apiClient.post(`/jobs/${id}/output-lot`, data),
  },

  /* -------- SCRAP -------- */
  scrapEntries: {
    list: (params) => apiClient.get("/scrap-entries", { params }),
    create: (data) => apiClient.post("/scrap-entries", data),
  },

  /* -------- CANONICAL SCRAP DISPATCH (Phase-B/C) -------- */
  scrapDispatchBatches: {
    list: (params) => apiClient.get("/scrap-dispatch-batches", { params }),
    create: (data) => apiClient.post("/scrap-dispatch-batches", data),
  },

  /* -------- DISPATCH -------- */
  dispatches: {
    list: (params) => apiClient.get("/dispatches", { params }),
    create: (data) => apiClient.post("/dispatches", data),
  },

  /* -------- DIES -------- */
  dies: {
    list: (params) => apiClient.get("/dies", { params }),
    create: (data) => apiClient.post("/dies", data),
  },

  /* -------- DIE EVENTS -------- */
  dieEvents: {
    list: (params) => apiClient.get("/die-events", { params }),
    create: (data) => apiClient.post("/die-events", data),
  },

  /* -------- ACCOUNTS -------- */
  accounts: {
    getMonthlySummary: (params) => apiClient.get("/accounts/monthly-summary", { params }),
    listPayments: (params) => apiClient.get("/accounts/payments", { params }),
    createPayment: (data) => apiClient.post("/accounts/payments", data),
  },

  /* -------- OCR (Phase-3A) -------- */
  ocr: {
    extract: (formData) => apiClient.post("/ocr/extract", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
    feedback: (data) => apiClient.post("/ocr/feedback", data),
    listFeedback: (params) => apiClient.get("/ocr/feedback", { params }),
    prefill: (data) => apiClient.post("/ocr/prefill", data),
  },
};

export const reportClientError = async (payload) => {
  try {
    await apiClient.post("/client-errors", payload);
  } catch {
    // Best-effort logging only.
  }
};

export default api;
